import { perf } from '../debug/perf/PerfDiag';
import { perfToggles } from '../debug/perf/PerfToggles';

export interface GameLoopOptions {
  fixedHz: number;
  update: (dt: number, clock: GameLoopStepClock) => void;
  /**
   * Render callback. `alpha` is the [0, 1) interpolation factor between the
   * previous and current physics step. `frameDt` is the real wallclock time
   * (seconds) since the previous render. Use the real value for perf/quality
   * control loops; callers that drive visual springs should clamp it before
   * applying it so a render hitch does not become a visual lurch. `clock`
   * is the loop-owned time source; use it for gameplay visuals instead of
   * reading `performance.now()` directly.
   */
  render: (alpha: number, frameDt: number, clock: GameLoopRenderClock) => void;
  /**
   * Maximum fixed updates to run during one RAF tick. When rendering stalls,
   * excess accumulated time is intentionally dropped so gameplay does not
   * apply a long burst of old input and collisions in a single visible frame.
   */
  maxSubsteps?: number;
  /**
   * Render cadence while `paused` is true. Physics is already stopped in that
   * state; throttling render keeps title/pause screens from burning the full
   * 3D scene at display refresh rate behind static DOM UI.
   */
  pausedRenderHz?: number;
  /**
   * Maximum active render cadence. Physics still ticks from RAF timing, but
   * render can be skipped to give the GPU idle time on laptops/mobile.
   * Pass <= 0 or Infinity for uncapped.
   */
  maxRenderHz?: number;
  /**
   * A frame is "slow" if its wallclock dt exceeded this many fixed steps.
   * Defaults to 1.5 — i.e. a frame that took 25ms on a 60Hz physics rate.
   * Caller can read `slowFrameCount` to scale quality down when this rises.
   */
  slowFrameThresholdSteps?: number;
  /**
   * Called at the top of every RAF callback, before any pause/skip gating.
   * Use for diagnostics that need to observe the raw browser frame cadence
   * even when the engine chooses not to render this tick. Must be cheap —
   * runs every browser frame.
   */
  onRaf?: (now: number) => void;
}

export interface GameLoopStepClock {
  /** Seconds advanced by completed fixed updates. */
  fixedTime: number;
  /** Monotonic RAF timestamp in seconds, for systems that need wall time. */
  wallTime: number;
}

export interface GameLoopRenderClock extends GameLoopStepClock {
  /**
   * Seconds advanced by fixed updates plus the interpolation accumulator.
   * This is the universal gameplay visual clock: it pauses when physics
   * pauses, honors dropped catch-up time, and is independent from render
   * cadence.
   */
  renderTime: number;
}

export class GameLoop {
  private accumulator = 0;
  private lastTime = 0;
  private fixedTime = 0;
  private rafId = 0;
  private running = false;
  paused = false;
  // Time dilation: physics timestep is scaled by `dilationCurrentScale`,
  // which holds at `dilationTargetScale` for `dilationHoldRemainingMs` then
  // eases back to 1.0 over `dilationRampOutTotalMs`. Render keeps full real
  // time so visuals & audio stay smooth — only the physics tick rate slows.
  // Setting target=0 with a ramp produces "soft hitstop" (gradual freeze
  // and release) which looks like impact, not a stutter.
  private dilationTargetScale = 1;
  private dilationHoldRemainingMs = 0;
  private dilationRampOutRemainingMs = 0;
  private dilationRampOutTotalMs = 60;

  /** Wallclock seconds elapsed during the most recent rendered frame. */
  lastFrameDt = 0;
  /** Rolling count of frames that exceeded the slow-frame threshold. */
  slowFrameCount = 0;
  /** Total rendered frames since `start()`. Excludes frames skipped by the render cap. */
  renderedFrameCount = 0;
  /** Total RAF callbacks that did not draw because the render cap was active. */
  renderSkipCount = 0;

  private readonly stepDt: number;
  private readonly maxSubsteps: number;
  private readonly slowFrameDt: number;
  private readonly pausedRenderIntervalMs: number;
  private lastPausedRenderTime = 0;
  private maxRenderHz: number;
  private renderIntervalMs: number;
  private lastRenderTime = 0;
  private pendingRenderDt = 0;
  private renderCreditMs = 0;
  // Exponential-mean of recent RAF deltas (ms). Used to detect when the
  // monitor is already running at or below the render cap, in which case
  // the cap must be a no-op — otherwise rAF jitter trips the skip predicate
  // and we drop perfectly good frames at the display rate. See
  // shouldSkipRender below for why this matters.
  private recentRafDtMs = 0;

  constructor(private readonly opts: GameLoopOptions) {
    this.stepDt = 1 / opts.fixedHz;
    this.maxSubsteps = opts.maxSubsteps ?? 5;
    this.slowFrameDt = this.stepDt * (opts.slowFrameThresholdSteps ?? 1.5);
    const pausedRenderHz = opts.pausedRenderHz ?? 10;
    this.pausedRenderIntervalMs = pausedRenderHz > 0 ? 1000 / pausedRenderHz : Number.POSITIVE_INFINITY;
    this.maxRenderHz = normalizeRenderHz(opts.maxRenderHz);
    this.renderIntervalMs = renderIntervalMsFor(this.maxRenderHz);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.lastRenderTime = 0;
    this.pendingRenderDt = 0;
    this.renderCreditMs = 0;
    this.recentRafDtMs = 0;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  setMaxRenderHz(maxRenderHz: number): void {
    this.maxRenderHz = normalizeRenderHz(maxRenderHz);
    this.renderIntervalMs = renderIntervalMsFor(this.maxRenderHz);
    this.lastRenderTime = 0;
    this.pendingRenderDt = 0;
    this.renderCreditMs = 0;
    this.recentRafDtMs = 0;
  }

  getMaxRenderHz(): number {
    return this.maxRenderHz;
  }

  /** Smoothed RAF cadence (ms). 0 until at least one RAF dt has been sampled. */
  getRecentRafDtMs(): number {
    return this.recentRafDtMs;
  }

  /**
   * Scale physics dt to `scale` (0..1) for `holdMs`, then ease back to 1.0
   * over `rampOutMs`. Render & audio stay on real time — only the physics
   * step rate slows. Use scale ~0.3 for "soft hitstop" on impacts; scale = 0
   * is a hard freeze. Stronger requests override weaker ones; all timers
   * extend rather than reset, so back-to-back impacts compound cleanly.
   */
  applyTimeDilation(scale: number, holdMs: number, rampOutMs: number = 60): void {
    if (!Number.isFinite(scale) || scale < 0 || scale >= 1) return;
    if (!Number.isFinite(holdMs) || holdMs <= 0) return;
    const ramp = Math.max(10, Number.isFinite(rampOutMs) ? rampOutMs : 60);
    if (scale < this.dilationTargetScale) this.dilationTargetScale = scale;
    if (holdMs > this.dilationHoldRemainingMs) this.dilationHoldRemainingMs = holdMs;
    if (ramp > this.dilationRampOutRemainingMs) {
      this.dilationRampOutRemainingMs = ramp;
      this.dilationRampOutTotalMs = ramp;
    }
  }

  /**
   * Hard freeze for `ms` (legacy; equivalent to applyTimeDilation(0, ms, 0)).
   * Prefer applyTimeDilation with a non-zero scale + ramp for impact feel.
   */
  applyHitstop(ms: number): void {
    this.applyTimeDilation(0, ms, 0);
  }

  /** True while time dilation is suppressing or slowing physics steps. */
  isHitstopped(): boolean {
    return this.dilationHoldRemainingMs > 0 || this.dilationRampOutRemainingMs > 0;
  }

  /** Current physics-time scale, 0..1. 1 = normal speed. */
  getTimeDilationScale(): number {
    if (this.dilationHoldRemainingMs > 0) return this.dilationTargetScale;
    if (this.dilationRampOutRemainingMs <= 0) return 1;
    const t = 1 - this.dilationRampOutRemainingMs / Math.max(1, this.dilationRampOutTotalMs);
    const eased = t * t * (3 - 2 * t);
    return this.dilationTargetScale + (1 - this.dilationTargetScale) * eased;
  }

  private tick = (now: number): void => {
    if (!this.running) return;
    this.opts.onRaf?.(now);
    const frameDt = Math.min(Math.max((now - this.lastTime) / 1000, 0), 0.25);
    this.lastTime = now;

    if (this.paused) {
      if (
        this.lastPausedRenderTime > 0
        && now - this.lastPausedRenderTime < this.pausedRenderIntervalMs
      ) {
        this.rafId = requestAnimationFrame(this.tick);
        return;
      }
      this.lastPausedRenderTime = now;
    } else {
      this.lastPausedRenderTime = 0;
    }

    this.lastFrameDt = frameDt;
    perf.frameBoundary(frameDt * 1000);
    if (frameDt > this.slowFrameDt) this.slowFrameCount += 1;

    // Track a smoothed RAF cadence so `shouldSkipRender` can detect when the
    // monitor is already running at or below the render cap. Ignore long
    // hitches (>50ms) so a single GC pause doesn't poison the mean.
    const frameDtMs = frameDt * 1000;
    if (frameDtMs > 0 && frameDtMs < 50) {
      this.recentRafDtMs = this.recentRafDtMs === 0
        ? frameDtMs
        : this.recentRafDtMs * 0.9 + frameDtMs * 0.1;
    }

    // Soft hitstop: physics dt is scaled (0..1) for the duration of the
    // dilation, then eases back to 1.0. Audio & render stay on real time.
    let dilationScale = 1;
    if (this.dilationHoldRemainingMs > 0) {
      dilationScale = this.dilationTargetScale;
      this.dilationHoldRemainingMs = Math.max(0, this.dilationHoldRemainingMs - frameDt * 1000);
    } else if (this.dilationRampOutRemainingMs > 0) {
      const t = 1 - this.dilationRampOutRemainingMs / Math.max(1, this.dilationRampOutTotalMs);
      const eased = t * t * (3 - 2 * t);
      dilationScale = this.dilationTargetScale + (1 - this.dilationTargetScale) * eased;
      this.dilationRampOutRemainingMs = Math.max(0, this.dilationRampOutRemainingMs - frameDt * 1000);
      if (this.dilationRampOutRemainingMs <= 0) this.dilationTargetScale = 1;
    } else if (this.dilationTargetScale !== 1) {
      this.dilationTargetScale = 1;
    }

    if (!this.paused) {
      this.accumulator += frameDt * dilationScale;
      let steps = 0;
      const maxSubsteps = perfToggles.physics.substepCap ? this.maxSubsteps : Infinity;
      while (this.accumulator >= this.stepDt && steps < maxSubsteps) {
        this.fixedTime += this.stepDt;
        this.opts.update(this.stepDt, {
          fixedTime: this.fixedTime,
          wallTime: now / 1000,
        });
        this.accumulator -= this.stepDt;
        steps += 1;
      }
      if (steps >= maxSubsteps && this.accumulator >= this.stepDt) {
        const droppedAccumulator = this.accumulator;
        perf.event('substepExhaust', {
          frameDtMs: frameDt * 1000,
          steps,
          droppedAccumulatorMs: droppedAccumulator * 1000,
        });
        this.accumulator = Math.min(this.accumulator, this.stepDt * 0.95);
      }
    }

    this.pendingRenderDt += frameDt;
    if (!this.paused) this.renderCreditMs += frameDt * 1000;
    if (!this.paused && this.shouldSkipRender()) {
      this.renderSkipCount += 1;
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    const alpha = this.paused ? 1 : this.accumulator / this.stepDt;
    const renderDt = this.pendingRenderDt;
    this.pendingRenderDt = 0;
    this.commitRender(now);
    this.renderedFrameCount += 1;
    this.opts.render(alpha, renderDt, {
      fixedTime: this.fixedTime,
      renderTime: this.paused ? this.fixedTime : this.fixedTime + this.accumulator,
      wallTime: now / 1000,
    });

    this.rafId = requestAnimationFrame(this.tick);
  };

  private shouldSkipRender(): boolean {
    if (!Number.isFinite(this.renderIntervalMs)) return false;
    if (this.lastRenderTime <= 0) return false;
    // Critical: when the display refresh is already at or below the render
    // cap, the cap must be a no-op. RAF on a 60Hz monitor jitters in the
    // ±0.5ms range; with a strict `credit < interval` check, the predicate
    // periodically fires and we drop frames the GPU could've drawn at
    // monitor rate. That's why the live PerfHud reads ~45fps while the rAF
    // histogram shows perfect 16.67ms cadence — we skip ~1 in 4 renders
    // ourselves. Bypass the cap entirely once we know rAF is keeping pace.
    if (
      this.recentRafDtMs > 0
      && this.recentRafDtMs + 1 >= this.renderIntervalMs
    ) {
      return false;
    }
    return this.renderCreditMs + 0.001 < this.renderIntervalMs;
  }

  private commitRender(now: number): void {
    this.lastRenderTime = now;
    if (!Number.isFinite(this.renderIntervalMs)) {
      this.renderCreditMs = 0;
      return;
    }
    this.renderCreditMs = Math.max(0, this.renderCreditMs - this.renderIntervalMs);
    if (this.renderCreditMs > this.renderIntervalMs) {
      this.renderCreditMs %= this.renderIntervalMs;
    }
  }
}

function normalizeRenderHz(maxRenderHz: number | undefined): number {
  if (typeof maxRenderHz !== 'number' || !Number.isFinite(maxRenderHz) || maxRenderHz <= 0) {
    return Number.POSITIVE_INFINITY;
  }
  return Math.max(1, maxRenderHz);
}

function renderIntervalMsFor(maxRenderHz: number): number {
  return Number.isFinite(maxRenderHz) ? 1000 / maxRenderHz : Number.POSITIVE_INFINITY;
}
